import { BaseEventAdapter } from './eventAdapter';
import type { AirtableEvent, EventItem, Place } from '../../types/shared/event';
import type { SupportedLanguages } from '../../i18n';
import { autoTranslator } from '../translations/autoTranslator';
import { logger } from '../logger';

export class AirtableEventAdapter extends BaseEventAdapter<any> {
  protected source = 'airtable' as const;

  public formatEvent(rawEvent: any): AirtableEvent {
    try {
      // Extraire les champs depuis fields si présent
      const fields = rawEvent.fields || rawEvent;

      // Gérer le champ image comme une pièce jointe Airtable
      let imageUrl = '';
      if (Array.isArray(fields.image) && fields.image.length > 0) {
        imageUrl = fields.image[0].url;
      }

      // S'assurer que la description existe
      const description = fields.description || fields.description_fr || '';
      if (!description) {
        logger.warn('Missing description', {
          category: 'API',
          data: { 
            recordId: rawEvent.id,
            name: fields.name
          }
        });
      }

      // Vérifier si une offre existe
      const offer = fields.offer || '';

      // Formater l'événement de base
      const baseEvent = {
        name: fields.name || '',
        descriptions: fields.descriptions || {},
        description, // Utiliser la description extraite
        offer, // Ajouter l'offre
        offers: fields.offers || {}, // Ajouter les traductions d'offres
        fulladress: fields.fulladress || '',
        telephone: fields.telephone || '',
        url: fields.url || '',
        itinerary: fields.itinerary || '',
        category: fields.category || 'Autre',
        type: fields.type || '',
        image: imageUrl,
        latitude: typeof fields.latitude === 'number' ? fields.latitude : parseFloat(fields.latitude) || 0,
        longitude: typeof fields.longitude === 'number' ? fields.longitude : parseFloat(fields.longitude) || 0,
        distance: typeof fields.distance === 'number' ? fields.distance : parseFloat(fields.distance) || 0,
        booking: fields.booking || '',
        price: fields.price || '',
        artiste: fields.artiste || '',
        place: fields.place || '',
        date: fields.date || '',
        hour: (fields.hour || '00:00').substring(0, 5),
        source: this.source,
        inHotel: !!fields.inHotel
      };

      // Valider l'événement
      if (!this.validateEvent(baseEvent)) {
        logger.warn('Invalid event data', {
          category: 'Validation',
          data: { 
            recordId: rawEvent.id,
            fields: baseEvent,
            missingFields: this.getMissingFields(baseEvent)
          }
        });
        throw new Error('Invalid event data');
      }

      // Créer l'événement final
      const event: AirtableEvent = {
        ...baseEvent,
        id: rawEvent.id,
        recordId: rawEvent.id,
        idShow: fields.idShow,
        source: this.source
      };

      return event;

    } catch (error) {
      logger.error('Error formatting event', {
        category: 'API',
        data: {
          error,
          recordId: rawEvent?.id,
          errorDetails: error instanceof Error ? {
            message: error.message,
            stack: error.stack
          } : error
        }
      });
      throw error;
    }
  }

  protected validateEvent(event: Partial<EventItem>): boolean {
    const requiredFields = [
      'name',
      'description',
      'category',
      'date',
      'place',
      'latitude',
      'longitude',
      'distance'
    ];

    const missingFields = requiredFields.filter(field => {
      const value = event[field as keyof EventItem];
      
      // Vérification spéciale pour les nombres
      if (field === 'latitude' || field === 'longitude' || field === 'distance') {
        return typeof value !== 'number' || isNaN(value);
      }
      
      return value === undefined || value === null || value === '';
    });

    if (missingFields.length > 0) {
      logger.warn('Missing required event fields', {
        category: 'Validation',
        data: { 
          missingFields,
          eventData: {
            name: event.name,
            category: event.category,
            date: event.date,
            place: event.place
          }
        }
      });
      return false;
    }

    return true;
  }

  private getMissingFields(event: Partial<EventItem>): string[] {
    const requiredFields = [
      'name',
      'description',
      'category',
      'date',
      'place',
      'latitude',
      'longitude',
      'distance'
    ];

    return requiredFields.filter(field => {
      const value = event[field as keyof EventItem];
      if (field === 'latitude' || field === 'longitude' || field === 'distance') {
        return typeof value !== 'number' || isNaN(value);
      }
      return value === undefined || value === null || value === '';
    });
  }

  public formatPlace(rawPlace: any): Place {
    return {
      name: rawPlace.name,
      latitude: rawPlace.latitude,
      longitude: rawPlace.longitude,
      distance: rawPlace.distance,
      fulladress: rawPlace.fulladress,
      itinerary: rawPlace.itinerary,
      shows: rawPlace.shows.map((show: any) => ({
        ...show,
        source: this.source,
        events: show.events.map((event: any) => this.formatEvent(event))
      }))
    };
  }

  public async translateDescription(
    description: string,
    targetLang: SupportedLanguages,
    context: { tableId: string; recordIds?: string[]; contentType?: 'description' | 'offer' }
  ): Promise<string> {
    try {
      // Vérifier les paramètres requis
      if (!description || !targetLang || !context.tableId) {
        logger.warn('Missing required translation parameters', {
          category: 'Translation',
          data: { 
            hasDescription: !!description,
            targetLang,
            hasTableId: !!context.tableId,
            contentType: context.contentType || 'description'
          }
        });
        return description;
      }

      // Utiliser l'autoTranslator avec les recordIds si disponibles
      return await autoTranslator.translateDescription(
        description,
        targetLang,
        {
          ...context,
          contentType: context.contentType || 'description',
          sectionType: 'event'
        }
      );
    } catch (error) {
      logger.error('Translation error in AirtableEventAdapter', {
        category: 'Translation',
        data: { 
          error, 
          description, 
          targetLang,
          contentType: context.contentType || 'description'
        }
      });
      return description;
    }
  }

  public generateShowId(event: EventItem): string {
    if (event.source !== 'airtable') {
      throw new Error('Invalid event source');
    }
    return event.idShow || `show_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public getFavoriteId(event: EventItem): string {
    if (event.source !== 'airtable') {
      throw new Error('Invalid event source');
    }
    return `airtable_${event.recordId}`;
  }
}

export const airtableEventAdapter = new AirtableEventAdapter();